import { Dialog, Disclosure, Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon, FilterIcon, XIcon } from "@heroicons/react/outline";
import { Dispatch, Fragment, SetStateAction, useState } from "react";

export interface Filter {
  id: string;
  name: string;
  component?: string;
  timeValue?: any;
  multiplier?: string;
  condition?: string;
  value?: any;
  options: {
    label: string | number;
    value: string | number;
    checked: boolean;
    isDisabled?: boolean;
  }[];
}

interface FilterBarProps {
  filters: Filter[];
  setFilters: Dispatch<SetStateAction<Filter[]>>;
  setIsShowUpgradeModal: (...args: any) => any;
}

const FilterBar = ({
  filters,
  setFilters,
  setIsShowUpgradeModal,
}: FilterBarProps) => {
  const [open, setOpen] = useState(false);
  const classNames = (...classes: string[]) => {
    return classes.filter(Boolean).join(" ");
  };
  return (
    <div className="flex justify-between items-center align-middle xl:py-4 lg:py-4">
      {/* Mobile filter dialog */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden xl:hidden"
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                <div className="flex items-center justify-between px-4">
                  <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md p-2 text-gray-400"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Filters */}
                <form className="mt-4">
                  {filters.map((section, sectionIdx) => (
                    <Disclosure
                      as="div"
                      key={section.name}
                      className="border-t border-gray-200 px-4 py-6"
                    >
                      {({ open }) => (
                        <>
                          <h3 className="-mx-2 -my-3 flow-root">
                            <Disclosure.Button className="flex w-full items-center justify-between px-2 py-3 text-sm text-gray-400">
                              <span className="font-medium text-gray-900">
                                {section.name}
                              </span>
                              <span className="ml-6 flex items-center">
                                <ChevronDownIcon
                                  className={classNames(
                                    open ? "-rotate-180" : "rotate-0",
                                    "h-5 w-5 transform"
                                  )}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel className="pt-6">
                            <div className="space-y-1">
                              {section.component === "date" ? (
                                <>
                                  <select
                                    className={`shrink py-2 pl-3 pr-10 sm:text-sm focus:border-orange-500 focus:ring-orange-500 border-gray-300 w-full`}
                                    onChange={(e) => {
                                      // Update this option in the filters
                                      const newFilters = [...filters];
                                      newFilters[sectionIdx].condition =
                                        e.target.value;
                                      setFilters(newFilters);
                                    }}
                                    defaultValue={section.condition}
                                  >
                                    <option selected disabled>
                                      Select an option
                                    </option>
                                    <option key="is_within" value="is_within">
                                      is within
                                    </option>
                                    <option key="is_after" value="is_after">
                                      is after
                                    </option>
                                  </select>
                                  <div className="relative w-full border-gray-300 flex focus:border-orange-500 focus:ring-orange-500 border">
                                    <input
                                      type="number"
                                      min="0"
                                      // step={getFilterStepValue(filter.property.value)}
                                      value={section.timeValue}
                                      onChange={(e) => {
                                        // Update this option in the filters
                                        const newFilters = [...filters];
                                        newFilters[sectionIdx].timeValue =
                                          e.target.value;
                                        setFilters(newFilters);
                                      }}
                                      className={`w-32 block grow sm:text-sm focus:border-orange-500 focus:ring-orange-500 border-transparent`}
                                      placeholder="Enter a value"
                                      // disabled={filter.isDisabled}
                                    />
                                    <div className="shrink inset-y-0 right-0 flex items-center">
                                      <select
                                        // disabled={filter.isDisabled}
                                        id="multiplier"
                                        name="multiplier"
                                        value={section.multiplier}
                                        onChange={(e) => {
                                          // Update this option in the filters
                                          const newFilters = [...filters];
                                          newFilters[sectionIdx].multiplier =
                                            e.target.value;
                                          setFilters(newFilters);
                                        }}
                                        className={`h-full border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 focus:border-orange-500 focus:ring-orange-500 sm:text-sm`}
                                      >
                                        <option value="m">minutes</option>
                                        <option value="h">hours</option>
                                        <option value="d">days</option>
                                      </select>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div className="space-y-3">
                                  {section.options.map(
                                    (option: any, optionIdx: any) => (
                                      <div
                                        key={option.value}
                                        className="flex items-center"
                                        onClick={() => {
                                          if (option.isDisabled === true) {
                                            setIsShowUpgradeModal(true);
                                          }
                                        }}
                                      >
                                        <input
                                          id={`filter-${section.id}-${optionIdx}`}
                                          name={`${section.id}[]`}
                                          defaultValue={option.value}
                                          type="checkbox"
                                          disabled={option.isDisabled === true}
                                          defaultChecked={option.checked}
                                          onChange={() => {
                                            // Update this option in the filters
                                            const newFilters = [...filters];
                                            newFilters[sectionIdx].options[
                                              optionIdx
                                            ].checked = !newFilters[sectionIdx]
                                              .options[optionIdx].checked;
                                            setFilters(newFilters);
                                          }}
                                          className={`h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 ${
                                            option.isDisabled === true
                                              ? "cursor-not-allowed bg-gray-100"
                                              : ""
                                          }`}
                                        />
                                        <label
                                          htmlFor={`filter-${section.id}-${optionIdx}`}
                                          className={`ml-3 capitalize whitespace-nowrap pr-6 text-sm font-medium ${
                                            option.isDisabled === true
                                              ? "cursor-not-allowed text-gray-400"
                                              : "text-gray-900"
                                          }`}
                                        >
                                          {option.label}
                                        </label>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Filters */}
      <div className="z-9 relative">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <button
            type="button"
            className="p-3 mr-2 my-2 border border-gray-300 shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 inline-flex items-center rounded-md text-sm font-medium leading-4 text-gray-600 hover:text-gray-900 focus:hover:text-gray-900 md:hidden lg:hidden xl:hidden"
            onClick={() => setOpen(true)}
          >
            <FilterIcon className="h-4 w-4 mr-1.5" />
            Filters
          </button>

          <div className="hidden lg:block xl:block px-4">
            <div className="flow-root">
              <Popover.Group className="-mx-4 flex items-center divide-x divide-gray-200">
                {filters.map((section, sectionIdx) => (
                  <Popover
                    key={section.name}
                    className="relative inline-block px-2 text-left"
                  >
                    <Popover.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                      <span className="capitalize">{section.name}</span>
                      {/* Count how many options are checked for this filter */}
                      {section.component == "date" ? (
                        <>
                          {section.timeValue != "" &&
                            section.condition != "" &&
                            section.multiplier != "" && (
                              <span className="ml-1.5 rounded bg-gray-200 py-0.5 px-1.5 text-xs font-semibold tabular-nums text-gray-700">
                                1
                              </span>
                            )}
                        </>
                      ) : (
                        <>
                          {section.options.filter(
                            (option: any) => option.checked
                          ).length > 0 && (
                            <span className="ml-1.5 rounded bg-gray-200 py-0.5 px-1.5 text-xs font-semibold tabular-nums text-gray-700">
                              {
                                section.options.filter(
                                  (option: any) => option.checked
                                ).length
                              }
                            </span>
                          )}
                        </>
                      )}
                      <ChevronDownIcon
                        className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Popover.Panel className="absolute max-h-72 overflow-y-scroll right-[-200px] z-10 mt-2 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <form className="space-y-1">
                          {section.component === "date" ? (
                            <>
                              <select
                                className={`shrink py-2 pl-3 pr-10 sm:text-sm focus:border-orange-500 focus:ring-orange-500 border-gray-300 w-full`}
                                onChange={(e) => {
                                  // Update this option in the filters
                                  const newFilters = [...filters];
                                  newFilters[sectionIdx].condition =
                                    e.target.value;
                                  setFilters(newFilters);
                                }}
                                defaultValue={section.condition}
                              >
                                <option value="" selected disabled>
                                  Select an option
                                </option>
                                <option key="is_within" value="is_within">
                                  is within
                                </option>
                                <option key="is_after" value="is_after">
                                  is after
                                </option>
                              </select>
                              <div className="relative w-full border-gray-300 flex focus:border-orange-500 focus:ring-orange-500 border">
                                <input
                                  type="number"
                                  min="0"
                                  // step={getFilterStepValue(filter.property.value)}
                                  value={section.timeValue}
                                  onChange={(e) => {
                                    // Update this option in the filters
                                    const newFilters = [...filters];
                                    newFilters[sectionIdx].timeValue =
                                      e.target.value;
                                    setFilters(newFilters);
                                  }}
                                  className={`w-32 block grow sm:text-sm focus:border-orange-500 focus:ring-orange-500 border-transparent`}
                                  placeholder="Enter a value"
                                  // disabled={filter.isDisabled}
                                />
                                <div className="shrink inset-y-0 right-0 flex items-center">
                                  <select
                                    // disabled={filter.isDisabled}
                                    id="multiplier"
                                    name="multiplier"
                                    value={section.multiplier}
                                    onChange={(e) => {
                                      // Update this option in the filters
                                      const newFilters = [...filters];
                                      newFilters[sectionIdx].multiplier =
                                        e.target.value;
                                      setFilters(newFilters);
                                    }}
                                    className={`h-full border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 focus:border-orange-500 focus:ring-orange-500 sm:text-sm`}
                                  >
                                    <option value="m">minutes</option>
                                    <option value="h">hours</option>
                                    <option value="d">days</option>
                                  </select>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="space-y-4">
                              {section.options.map(
                                (option: any, optionIdx: any) => (
                                  <div
                                    key={option.value}
                                    className="flex items-center"
                                    onClick={() => {
                                      if (option.isDisabled === true) {
                                        setIsShowUpgradeModal(true);
                                      }
                                    }}
                                  >
                                    <input
                                      id={`filter-${section.id}-${optionIdx}`}
                                      name={`${section.id}[]`}
                                      defaultValue={option.value}
                                      type="checkbox"
                                      disabled={option.isDisabled === true}
                                      defaultChecked={option.checked}
                                      onChange={() => {
                                        // Update this option in the filters
                                        const newFilters = [...filters];
                                        newFilters[sectionIdx].options[
                                          optionIdx
                                        ].checked = !newFilters[sectionIdx]
                                          .options[optionIdx].checked;
                                        setFilters(newFilters);
                                      }}
                                      className={`h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 ${
                                        option.isDisabled === true
                                          ? "cursor-not-allowed bg-gray-100"
                                          : ""
                                      }`}
                                    />
                                    <label
                                      htmlFor={`filter-${section.id}-${optionIdx}`}
                                      className={`ml-3 capitalize whitespace-nowrap pr-6 text-sm font-medium ${
                                        option.isDisabled === true
                                          ? "cursor-not-allowed text-gray-400"
                                          : "text-gray-900"
                                      }`}
                                    >
                                      {option.label}
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </form>
                      </Popover.Panel>
                    </Transition>
                  </Popover>
                ))}
              </Popover.Group>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
